//CSVメール送信予約ページ
import React, { Fragment, useState } from 'react';
import {
  Typography,
  Button,
  Form,
  message,
  Input,
  Select,
  Modal,
  DatePicker,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import Dropzone from 'react-dropzone';
import Axios from 'axios';
import config from '../../Config/key';
import { DEFAULT_BCC_EMAIL } from '../../Config/mailConfig';
import Papa from 'papaparse';
import {
  decodeCsvBuffer,
  validateCsvHeaders,
  formatHeaderValidationError,
  extractAxiosErrorMessage,
} from '../../../utils/csvUploadHelper';
import moment from 'moment';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { confirm } = Modal;

// CSV に必須の列（email のみ）
const REQUIRED_COLUMNS = ['email'];

const CSVMailSendReservatePage = (props) => {
  const [fileData, setFileData] = useState();
  const [fileJSON, setFileJSON] = useState();
  const [mtInfo, setMtInfo] = useState([]);
  const [ccMailValue, setCCMailValue] = useState('');
  const [bccMailValue, setBCCMailValue] = useState(DEFAULT_BCC_EMAIL);
  const [survey, setSurvey] = useState([]);
  const [selSurvey, setSelSurvey] = useState([]);
  const [selMtId, setSelMtId] = useState('');
  const [submitButtonDisable, setSubmitButtonDisable] = useState(true);
  const [sendDateTime, setSendDateTime] = useState(null);

  const onDrop = async (files) => {
    const file = files[0];
    const reader = new FileReader();
    reader.onabort = () => console.log('file reading was aborted');
    reader.onerror = () => console.log('file reading failed');
    reader.onload = (e) => {
      // decodeCsvBuffer で文字コード自動判定 + 先頭 BOM 除去まで一括処理
      const unicodeString = decodeCsvBuffer(e.target.result);
      Papa.parse(unicodeString, {
        header: true,
        dynamicTyping: false,
        skipEmptyLines: true,
        complete: async (results) => {
          // 必須列の存在検証（BOM は decodeCsvBuffer で除去済み、その他不可視文字を捕捉）
          const headers = (results.meta && results.meta.fields) || [];
          const v = validateCsvHeaders(headers, REQUIRED_COLUMNS);
          if (!v.ok) {
            message.error({
              content: formatHeaderValidationError(v),
              key: 'csvupload',
              duration: 10,
            });
            setFileJSON(undefined);
            setFileData(
              'エラー：CSVヘッダーを確認の上、再アップロードしてください。'
            );
            return;
          }
          setFileData(unicodeString);
          setFileJSON(results.data);
        },
      });
    };
    reader.readAsArrayBuffer(file);
    const [mtInfo, surveyInfo] = await Promise.all([
      Axios.get('/api/mailTemplate/'),
      Axios.get('/api/survey/'),
    ]);
    setMtInfo(mtInfo.data.mailTemplate);
    setSurvey(surveyInfo.data.survey);
  };

  const ymdhms = () => {
    var date = new Date();
    return (
      date.getFullYear() +
      ('0' + (date.getMonth() + 1)).slice(-2) +
      ('0' + date.getDate()).slice(-2) +
      ('0' + date.getHours()).slice(-2) +
      ('0' + date.getMinutes()).slice(-2) +
      ('0' + date.getSeconds()).slice(-2)
    );
  };

  const sendMailReservateHandler = async () => {
    let mailList = {};
    try {
      message.loading({ content: 'メール送信予約中...', key: 'csvupload' });

      // CSV が正常に読み込まれているかを確認（必須列検証で弾かれた場合は fileJSON が undefined）
      if (!fileJSON || !Array.isArray(fileJSON) || fileJSON.length === 0) {
        message.error({
          content:
            '有効なCSVファイルがアップロードされていません。CSVを再確認してください。',
          key: 'csvupload',
          duration: 3,
        });
        throw new Error('CSVデータがありません。');
      }

      if (!selMtId) {
        message.error({
          content: 'メールテンプレートを選択してください。',
          key: 'csvupload',
          duration: 2,
        });
        throw new Error('テンプレートIDがありません。');
      }

      if (!sendDateTime) {
        message.error({
          content: '送信予定日時を選択してください。',
          key: 'csvupload',
          duration: 2,
        });
        throw new Error('送信予定日時がありません。');
      }

      const selMtInfo = mtInfo.find((item) => item._id === selMtId);

      let personalizations = [];
      await fileJSON.map((record) => {
        let ccText;
        if (record.cc) {
          ccText = ccMailValue + ';' + record.cc;
        } else {
          ccText = ccMailValue;
        }
        const ccString = ccText
          .replace(/\s+/g, '')
          .replace(/(^[;]+)|([;]+$)/g, '');
        let bccText;
        if (record.bcc) {
          bccText = bccMailValue + ';' + record.bcc;
        } else {
          bccText = bccMailValue + ';';
        }
        const bccString = bccText
          .replace(/\s+/g, '')
          .replace(/(^[;]+)|([;]+$)/g, '');
        let cc = [];
        let bcc = [];
        let ccArr = [];
        let bccArr = [];
        if (ccString) {
          cc = ccString.split(';');
          cc.map((element) => {
            ccArr.push({ email: element });
          });
        }
        if (bccString) {
          bcc = bccString.split(';');
          bcc.map((element) => {
            bccArr.push({ email: element });
          });
        }
        personalizations.push({
          to: record.email,
          cc: ccArr,
          bcc: bccArr,
          substitutions: record,
        });
      });

      // メール配信時に最新のテンプレートを参照するため、対象者情報のみ保存
      mailList = {
        personalizations: personalizations,
      };

      const reservateResult = await Axios.post('/api/sendMail/reservate', {
        group_name: '',
        survey_name: '',
        season_name: '',
        set_name: '',
        mt_name: selMtInfo.mt_name,
        mt_id: selMtId, // テンプレートIDを保存（発送時点で参照）
        mail_list: mailList,
        send_date_time: sendDateTime.toDate(),
        isAnswerd: 'CSV',
      });

      if (reservateResult.data.success) {
        setFileData('メール送信予約を完了しました。');
        message.success({
          content: 'メール送信予約完了！',
          key: 'csvupload',
          duration: 2,
        });
        return reservateResult;
      } else {
        message.error({
          content: 'メール送信予約に失敗しました。',
          key: 'csvupload',
          duration: 3,
        });
        throw new Error('メール送信予約に失敗しました。');
      }
    } catch (error) {
      console.log({ error });
      // バックエンドの具体的なエラー本文を抽出して表示（運用担当が pm2 logs 参照不要）
      message.error({
        content: extractAxiosErrorMessage(
          error,
          'メール送信予約に失敗しました。'
        ),
        key: 'csvupload',
        duration: 5,
      });
    }
  };

  const onChangeMtInfo = (value) => {
    if (value) {
      setSelMtId(value);
      setSubmitButtonDisable(false);
    }
  };

  const showConfirm = async () => {
    confirm({
      title: 'メールを本当に予約配信しますか？',
      content:
        'メール配信予約処理が完了し、ホームへ自動遷移されるまで、そのままお待ちください。',
      async onOk() {
        const result = await sendMailReservateHandler();
        if (result) {
          message.success({
            content: 'メールの配信予約が完了しました。ホームに戻ります。',
            key: 'csvupload',
            duration: 2,
          });
          props.history.push('/');
        } else {
          message.error({
            content: 'メール送信予約に失敗しました。',
            key: 'csvupload',
            duration: 2,
          });
          throw new Error('メール送信予約に失敗しました。');
        }
      },
      async onCancel() {
        message.success({
          content: 'メール配信予約を取り消しました。',
          key: 'csvupload',
          duration: 2,
        });
      },
    });
  };

  const onChangeSurvey = (v) => {
    setSelSurvey(survey.find((item) => item.survey_code === v));
    setSelMtId('');
  };

  const onChangeCCHandler = (e) => {
    setCCMailValue(e.currentTarget.value);
  };

  const onChangeBCCHandler = (e) => {
    setBCCMailValue(e.currentTarget.value);
  };

  const onChangeDateTime = (date, dateString) => {
    setSendDateTime(date);
  };

  return (
    <Fragment>
      <div style={{ maxWidth: '700px', margin: '2rem auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Title level={2}>配信予約（CSV）</Title>
        </div>
        <Form onSubmit={showConfirm}>
          <br />
          <Title style={{ color: 'var(--brand-primary)' }}>❶</Title>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Dropzone onDrop={onDrop} multiple={false} maxSize={1000000000}>
              {({ getRootProps, getInputProps }) => (
                <div
                  style={{
                    width: '300px',
                    height: '240px',
                    border: '1px solid lightgray',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  {...getRootProps()}
                >
                  <input {...getInputProps()} />
                  <PlusOutlined style={{ fontSize: '3rem' }} />
                </div>
              )}
            </Dropzone>
            <div
              style={{
                width: '300px',
                height: '240px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text mark>
                CSVファイルをDrag &
                Dropするか、+を押してファイルを選択してください。
              </Text>
            </div>
          </div>
          <br />
          <div>
            <Title style={{ color: 'var(--brand-primary)' }}>❷</Title>
            <Text strong>メールテンプレートを選択してください。（必須）</Text>
            <br />
            <br />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Select
                showSearch
                style={{
                  width: 400,
                }}
                placeholder="サーベイ選択"
                optionFilterProp="children"
                onChange={onChangeSurvey}
                value={selSurvey.survey_code}
                filterOption={(input, option) =>
                  option.children
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {survey.map((element) => {
                  return (
                    <Option value={element.survey_code} key={element._id}>
                      {element.survey_name}
                    </Option>
                  );
                })}
              </Select>
              <Select
                showSearch
                style={{
                  marginLeft: 10,
                  marginRight: 10,
                  marginBottom: 10,
                  width: 600,
                }}
                placeholder="テンプレート選択"
                optionFilterProp="children"
                onChange={onChangeMtInfo}
                value={selMtId}
                filterOption={(input, option) =>
                  option.children
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {mtInfo.map((element) => {
                  if (element.survey_code === selSurvey.survey_code) {
                    return (
                      <Option value={element._id} key={element._id}>
                        {element.mt_name}
                      </Option>
                    );
                  }
                })}
              </Select>
            </div>
            <br />
          </div>
          <Title style={{ color: 'var(--brand-primary)' }}>❸</Title>
          <Text strong>送信予定日時を選択してください。（必須）</Text>
          <br />
          <br />
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            placeholder="送信予定日時を選択"
            onChange={onChangeDateTime}
            style={{
              marginBottom: 10,
              width: 200,
            }}
          />
          <br />
          <br />
          <Title style={{ color: 'var(--brand-primary)' }}>❹</Title>
          <Text strong>CC・BCCを入力してください。</Text> <br />
          <br />
          CC：
          <Input
            placeholder="メールアドレスを「;」区切りで入力してください"
            allowClear
            style={{
              marginLeft: 10,
              marginRight: 10,
              marginBottom: 10,
              width: 350,
            }}
            onChange={onChangeCCHandler}
            value={ccMailValue}
          />{' '}
          <br />
          BCC：
          <Input
            placeholder="メールアドレスを「;」区切りで入力してください"
            allowClear
            style={{
              marginLeft: 10,
              marginRight: 10,
              marginBottom: 10,
              width: 350,
            }}
            onChange={onChangeBCCHandler}
            value={bccMailValue}
          />
          <Title style={{ color: 'var(--brand-primary)' }}>❺</Title>
          <Button
            type="primary"
            size="large"
            onClick={showConfirm}
            disabled={submitButtonDisable || !sendDateTime}
          >
            メール発送予約
          </Button>
        </Form>
        <TextArea rows={20} value={fileData} />
      </div>
    </Fragment>
  );
};

export default CSVMailSendReservatePage;
